/**
 * SMOOTH TEXT (MODES.md §10.2a) — streamed text arrives in bursts (≤ 4 updates / s); revealing it character by
 * character on animation frames makes it read like it's being written, never jumpy. The reveal speeds up with how far
 * behind it is (it always catches up within ~0.6 s) and jumps straight to the end for reduced-motion readers or when
 * the new text isn't a continuation of what's shown.
 *
 *   const shown = useSmoothText(target);
 */
import { useEffect, useRef, useState } from 'react';

const reduced = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function useSmoothText(target = '', { catchUpMs = 600 } = {}) {
  const [shown, setShown] = useState(target || '');
  const cur = useRef(target || '');
  const raf = useRef(0);
  useEffect(() => {
    const goal = String(target || '');
    if (!goal.startsWith(cur.current) || reduced() || typeof requestAnimationFrame !== 'function') {
      cur.current = goal; setShown(goal); return undefined;
    }
    if (goal.length === cur.current.length) return undefined;              // nothing to reveal
    let last = 0;
    const step = (t) => {
      const dt = last ? t - last : 16;
      last = t;
      const behind = goal.length - cur.current.length;
      if (behind <= 0) return;
      const n = Math.max(1, Math.ceil((behind * dt) / catchUpMs));
      cur.current = goal.slice(0, cur.current.length + n);
      setShown(cur.current);
      raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [target, catchUpMs]);
  return shown;
}
